/* eslint-disable no-nested-ternary */
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import P1 from '@base/P1/P1';
import H2 from '@base/H2/H2';
import H6 from '@base/H6/H6';
import Button from '@base/Button/Button';
import { emptyObject } from '@constants/empty';
import CheckBig from '@icon/CheckBig';
import dateHelper from '@utils/dates';
import { useIsMobile } from '@stores/AppCommonData';
import { EDUCATOR_UID, EDUCATOR_USERNAME } from '@events/segmentProps';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import User from '@models/User';
import { breakpoints, Spacings, Fonts } from '@styles/index';
import { getMe } from '@selectors/learner';
import useProfile from '@api/hooks/profile/useProfile';
import { segment } from '@actions/analytics';
import useEducatorDetailsSegmentData from '@hooks/useEducatorDetailsSegmentData';
import { getIsSubscribedToOffline } from '@selectors/subscription';
import { useSnackbar } from '@stores/snackbar';
import {
  fetchProfileInfo,
  toggleEducatorFollowingStatus
} from '@actions/educator';
import {
  EDUCATOR_FOLLOWED,
  EDUCATOR_UNFOLLOWED,
  EDUCATOR_PROFILE_VIEWED,
  EDUCATOR_PROFILE
} from '@constants/segment';
import Dynamic from '@base/Dynamic/Dynamic';
import { capitalize } from '@utils/textHelper';
import { EDUCATOR_PAGE } from '@constants/appFlyerLinks';
import useOneLinkSmartScript from '@hooks/useOneLinkSmartScript';
import { ExEducatorDescription } from '@constants/index';
import { educatorTypes } from '@actions-constants/index';
import useSelectedGoal, {
  useSelectedGoalUID
} from '@api/hooks/topology/useSelectedGoal';
import { useRouter } from 'next/router';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { EDUCATOR_TYPES } from '@cont/Batch/utils';
import EducatorStats from './EducatorStats';
import EducatorDedicationStats from './EducatorDedicationStats';
import EducatorAvatar from './EducatorAvatar';
import EducatorLevelTag from './EducatorLevelTag';

const SubHeader = Dynamic(() => import('./SubHeader'));

const Wrapper = styled.div`
  grid-column: ${({ $isExEducator }) => ($isExEducator ? '1/21' : '1/24')};
  grid-column-start: ${({ $isExEducator }) => ($isExEducator ? 3 : 1)};
  background: var(--color-base-0);
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: span 10;
    padding: 0;
  }
`;

const HeaderWrapper = styled.div`
  background-color: var(--color-base-1);
  padding-top: ${Spacings.SPACING_10B};
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  grid-column: span 24;
  border-radius: ${Spacings.SPACING_2B};
  box-shadow: ${boxShadowGenerator()};
  z-index: 1;
  padding-bottom: ${Spacings.SPACING_8B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: span 10;
    box-shadow: none;
    grid-template-columns: repeat(10, 1fr);
    background: var(--color-base-0);
    padding: 24px 0;
  }
`;

const EducatorDetail = styled.div`
  grid-column: 1/25;
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: span 10;
    grid-template-columns: repeat(10, 1fr);
    grid-column-gap: ${Spacings.SPACING_2B};
  }
`;

const EducatorInfo = styled.div`
  grid-column: ${({ $isExEducator }) => ($isExEducator ? '2 / 20' : '2 / 18')};
  display: flex;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: span 10;
    flex-direction: column;
  }
`;

const EducatorDetails = styled.div`
  margin-left: ${Spacings.SPACING_6B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-left: 0;
    margin-top: 24px;
  }
`;

const EducatorTitle = styled.div`
  align-items: center;
  display: flex;
  margin-bottom: ${Spacings.SPACING_2B};
`;

const Title = styled(H2)`
  display: inline-block;
  color: var(--color-text-primary);
  line-height: ${Fonts.HEADER_LINE_HEIGHT};
`;

const VerifiedIcon = styled.div`
  padding-left: ${Spacings.SPACING_3B};
  display: inline-block;
`;

const FollowButton = styled(Button)`
  grid-column: 20/24;
  margin-top: ${Spacings.SPACING_2B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: span 10;
    margin-top: ${Spacings.SPACING_6B};
    width: 100%;
    padding: 0;
  }
`;

const StatsContainer = styled.div`
  margin-top: ${Spacings.SPACING_6B};
  max-height: 100px;
  opacity: 1;
  display: flex;
  grid-column: 2/25;
  transition: all 300ms ease;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    max-height: none;
    grid-column: span 10;
    display: grid;
    grid-template-columns: repeat(9, 1fr);
    grid-column-gap: 0;
    & > div:first-child {
      margin-right: 8px;
    }
    & > div:nth-child(2) {
      margin-right: 8px;
    }
    & > div:last-child {
      margin-right: 0;
      width: 100%;
    }
  }
`;

const AnimateBio = styled(P1)`
  max-height: 100px;
  opacity: 1;
  transition: all 300ms ease;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    font-size: 12px;
  }
`;

const StyledH6 = styled(H6)`
  color: var(--color-text-secondary);
  margin-bottom: ${Spacings.SPACING_2B};
`;

const EducatorHeader = ({ username, isPlatinumEducator }) => {
  const dispatch = useDispatch();
  const me = useSelector(getMe);
  const myUsername = me.username?.toLowerCase();
  const selectedGoalUID = useSelectedGoalUID();
  const isOfflineSubscription = useSelector(
    getIsSubscribedToOffline(selectedGoalUID)
  );
  const { selectedGoal, isValidating } = useSelectedGoal();
  const { profileData: educator, mutate: revalidateEducatorProfile } =
    useProfile(username, {
      goalUid: selectedGoalUID,
      revalidateOnMount: true
    });
  const isMobile = useIsMobile();
  const isFollowing = educator?.isFollowing;
  const isLoggedIn = useMyInfo(getIsLoggedIn);

  const { query, isReady } = useRouter();
  const segmentData = useEducatorDetailsSegmentData({
    educatorProfile: educator
  });

  const appDownloadLink = useOneLinkSmartScript(EDUCATOR_PAGE);

  const { open: showSnackbar } = useSnackbar();
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);

  useEffect(() => {
    if (isReady && !isValidating) {
      segment.track(EDUCATOR_PROFILE_VIEWED, {
        ...segmentData,
        last_primary_source: query.view ?? 'NA',
        goal_name: selectedGoal?.name ?? 'NA',
        goal_uid: selectedGoal?.uid ?? 'NA',
        batch_group_type: query.source === 'CGP' ? 'Offline' : 'NA'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, isValidating, selectedGoal?.name]);

  const {
    avatar,
    firstName,
    lastName,
    bio: description,
    isVerifiedEducator,
    isExEducator,
    dateJoined,
    isEducator,
    educatorTypeV2
  } = educator || {};
  const name = `${capitalize(firstName)} ${capitalize(lastName)}`;

  const isPartnerEducator = EDUCATOR_TYPES.PARTNER === educatorTypeV2;

  const onClickFollow = (user, follow, userUID = null) => {
    const eventName = follow ? EDUCATOR_FOLLOWED : EDUCATOR_UNFOLLOWED;
    const newSegmentData = {};
    if (user) newSegmentData[EDUCATOR_USERNAME] = user;
    if (userUID) newSegmentData[EDUCATOR_UID] = userUID;
    newSegmentData.last_primary_source = EDUCATOR_PROFILE;
    dispatch(toggleEducatorFollowingStatus(user, follow)).then((res) => {
      if (res.type === `${educatorTypes.TOGGLE_FOLLOWING_STATUS}_FAILURE`) {
        showSnackbar({
          message: res.error?.message || res.error?.detail,
          type: 'error'
        });
      }
      revalidateEducatorProfile();
      dispatch(fetchProfileInfo(username));
      segment.track(eventName, { ...segmentData, ...newSegmentData });
    });
  };

  const handleOnClickFollow = () => {
    if (isMobile) {
      window.open(appDownloadLink, '_self');
      return;
    }
    if (!isLoggedIn) {
      openLoginDrawer();
      return;
    }
    onClickFollow(educator?.username, !isFollowing);
  };

  const dateOfJoiningString = dateJoined
    ? `Joined Unacademy in ${dateHelper.getMonthString(
        dateJoined
      )} ${dateHelper.getYear(dateJoined)}`
    : null;
  return (
    <Wrapper $isExEducator={isExEducator}>
      {!isPlatinumEducator && (
        <SubHeader onClickFollow={onClickFollow} username={username} />
      )}
      {educator && (
        <>
          <HeaderWrapper>
            <EducatorDetail>
              <EducatorInfo $isExEducator={isExEducator}>
                <EducatorAvatar
                  src={avatar}
                  isPlatinumEducator={isPlatinumEducator}
                  levelInfo={isExEducator ? emptyObject : educator?.levelInfo}
                  size="L"
                  isPartnerEducator={isPartnerEducator}
                />
                <EducatorDetails>
                  <EducatorTitle>
                    <Title>{name}</Title>
                    {isVerifiedEducator && (
                      <VerifiedIcon>
                        <CheckBig
                          size={Spacings.SPACING_6B}
                          color="var(--color-i-blue)"
                        />
                      </VerifiedIcon>
                    )}
                  </EducatorTitle>
                  {isExEducator && <StyledH6>Ex-educator</StyledH6>}
                  {!isPlatinumEducator && (
                    <EducatorLevelTag
                      levelInfo={
                        isExEducator ? emptyObject : educator?.levelInfo
                      }
                      isPartnerEducator={isPartnerEducator}
                    />
                  )}
                  <AnimateBio
                    color="var(--color-text-secondary)"
                    lineClamp={isMobile ? 2 : 0}
                  >
                    {isEducator
                      ? description
                      : isExEducator
                      ? ExEducatorDescription
                      : dateOfJoiningString}
                  </AnimateBio>
                </EducatorDetails>
              </EducatorInfo>
              {myUsername !== educator?.username?.toLowerCase() &&
                !isExEducator &&
                !isPlatinumEducator && (
                  <FollowButton
                    variant={isFollowing ? 'hollow' : 'filled'}
                    label={isFollowing ? 'Following' : 'Follow'}
                    onClick={handleOnClickFollow}
                  />
                )}
            </EducatorDetail>
            {!User.isLegendEducator(educator) && !isPlatinumEducator && (
              <StatsContainer>
                <EducatorStats
                  username={username}
                  isEducator={educator.isEducator}
                  isExEducator={educator.isExEducator}
                  onClickFollow={onClickFollow}
                />
                {!educator.isExEducator &&
                  !educator.isPartnerEducator &&
                  !isOfflineSubscription && (
                    <EducatorDedicationStats username={username} />
                  )}
              </StatsContainer>
            )}
          </HeaderWrapper>
        </>
      )}
    </Wrapper>
  );
};
export default EducatorHeader;

EducatorHeader.propTypes = {
  username: PropTypes.string.isRequired
};
